/**
 * Invoice Service
 * Handles invoice generation, management, and billing operations
 */

import { api_service, handleBlobError } from './api';
import {
  Invoice,
  InvoiceCreateData,
  InvoiceUpdateData,
  InvoiceFilters,
  InvoiceItem,
  InvoiceTemplate,
  InvoiceStatistics,
  InvoiceAgingReport,
  RevenueReport,
  BulkInvoiceGenerationResult,
  InvoicePreview,
  InvoiceValidation
} from '@/types/invoice_types';

// ============ TYPE DEFINITIONS ============

/**
 * Paginated response type
 */
export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

/**
 * Alias for invoice creation - used by components
 */
export type CreateInvoicePayload = InvoiceCreateData;

/**
 * Invoice email payload
 */
export interface InvoiceEmailPayload {
  invoice_id: number;
  recipient_email: string;
  subject?: string;
  message?: string;
  cc_emails?: string[];
}

/**
 * Invoice template configuration (simplified)
 */
export type InvoiceTemplateConfig = InvoiceTemplate;

// ============ INVOICE CRUD OPERATIONS ============

/**
 * Fetch all invoices with optional filters
 * @param filters - Optional filters for invoice list
 */
export const fetch_invoices = async (
  filters?: InvoiceFilters
): Promise<Invoice[]> => {
  try {
    const response = await api_service.get('/billing/invoices/', {
      params: filters
    });

    console.log("invoices", response)
    return response.results;
  } catch (error) {
    console.error('Error fetching invoices:', error);
    throw error;
  }
};

/**
 * Fetch paginated invoices
 * @param page - Page number
 * @param page_size - Items per page
 * @param filters - Optional filters
 */
export const get_invoices = async (
  page: number = 1,
  page_size: number = 20,
  filters?: InvoiceFilters
): Promise<PaginatedResponse<Invoice>> => {
  try {
    const response = await api_service.get('/billing/invoices/', {
      params: {
        page,
        page_size,
        ...filters
      }
    });
    return response.results;
  } catch (error) {
    console.error('Error fetching invoices:', error);
    throw error;
  }
};

/**
 * Fetch single invoice by ID
 * @param invoice_id - Invoice ID
 */
export const get_invoice = async (invoice_id: number): Promise<Invoice> => {
  try {
    const response = await api_service.get(`/billing/invoices/${invoice_id}/`);
    return response.results;
  } catch (error) {
    console.error('Error fetching invoice:', error);
    throw error;
  }
};

/**
 * Fetch invoice by invoice number
 * @param invoice_number - Invoice number
 */
export const get_invoice_by_number = async (
  invoice_number: string
): Promise<Invoice> => {
  try {
    const response = await api_service.get(`/billing/invoices/by-number/${invoice_number}/`);
    return response.results;
  } catch (error) {
    console.error('Error fetching invoice by number:', error);
    throw error;
  }
};

/**
 * Create new invoice
 * @param data - Invoice creation data
 */
export const create_invoice = async (
  data: CreateInvoicePayload
): Promise<Invoice> => {
  try {
    const response = await api_service.post('/billing/invoices/', data);
    return response.results;
  } catch (error) {
    console.error('Error creating invoice:', error);
    throw error;
  }
};

/**
 * Update existing invoice
 * @param invoice_id - Invoice ID
 * @param data - Invoice update data
 */
export const update_invoice = async (
  invoice_id: number,
  data: InvoiceUpdateData
): Promise<Invoice> => {
  try {
    const response = await api_service.patch(`/billing/invoices/${invoice_id}/`, data);
    return response.results;
  } catch (error) {
    console.error('Error updating invoice:', error);
    throw error;
  }
};

/**
 * Delete invoice
 * @param invoice_id - Invoice ID
 */
export const delete_invoice = async (invoice_id: number): Promise<void> => {
  try {
    await api_service.delete(`/billing/invoices/${invoice_id}/`);
  } catch (error) {
    console.error('Error deleting invoice:', error);
    throw error;
  }
};

// ============ INVOICE STATUS MANAGEMENT ============

/**
 * Update invoice status
 * @param invoice_id - Invoice ID
 * @param status - New status
 */
export const update_status = async (
  invoice_id: number,
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled'
): Promise<Invoice> => {
  try {
    const response = await api_service.patch(`/billing/invoices/${invoice_id}/status/`, {
      status
    });
    return response.results;
  } catch (error) {
    console.error('Error updating invoice status:', error);
    throw error;
  }
};

/**
 * Mark invoice as sent
 * @param invoice_id - Invoice ID
 */
export const mark_as_sent = async (invoice_id: number): Promise<Invoice> => {
  try {
    const response = await api_service.post(`/billing/invoices/${invoice_id}/mark-sent/`, {});
    return response.results;
  } catch (error) {
    console.error('Error marking invoice as sent:', error);
    throw error;
  }
};

/**
 * Mark invoice as paid
 * @param invoice_id - Invoice ID
 * @param payment_data - Optional payment data
 */
export const mark_as_paid = async (
  invoice_id: number,
  payment_data?: {
    amount: number;
    payment_date: string;
    payment_method: string;
  }
): Promise<Invoice> => {
  try {
    const response = await api_service.post(
      `/billing/invoices/${invoice_id}/mark-paid/`,
      payment_data || {}
    );
    return response.results;
  } catch (error) {
    console.error('Error marking invoice as paid:', error);
    throw error;
  }
};

/**
 * Cancel invoice
 * @param invoice_id - Invoice ID
 * @param reason - Cancellation reason
 */
export const cancel_invoice = async (
  invoice_id: number,
  reason?: string
): Promise<Invoice> => {
  try {
    const response = await api_service.post(`/billing/invoices/${invoice_id}/cancel/`, {
      reason
    });
    return response.results;
  } catch (error) {
    console.error('Error cancelling invoice:', error);
    throw error;
  }
};

// ============ INVOICE GENERATION ============

/**
 * Generate invoice from meter reading
 * @param reading_id - Meter reading ID
 */
export const generate_from_reading = async (
  reading_id: number
): Promise<Invoice> => {
  try {
    const response = await api_service.post('/billing/invoices/generate-from-reading/', {
      reading_id
    });
    return response.results;
  } catch (error) {
    console.error('Error generating invoice from reading:', error);
    throw error;
  }
};

/**
 * Bulk generate invoices from multiple readings
 * @param reading_ids - Array of reading IDs
 */
export const bulk_generate_invoices = async (
  reading_ids: number[]
): Promise<BulkInvoiceGenerationResult> => {
  try {
    const response = await api_service.post('/billing/invoices/bulk-generate/', {
      reading_ids
    });
    return response.results;
  } catch (error) {
    console.error('Error bulk generating invoices:', error);
    throw error;
  }
};

/**
 * Generate monthly invoices for all clients
 * @param month - Month (1-12)
 * @param year - Year
 */
export const generate_monthly_invoices = async (
  month: number,
  year: number
): Promise<BulkInvoiceGenerationResult> => {
  try {
    const response = await api_service.post('/billing/invoices/generate-monthly/', {
      month,
      year
    });
    return response.results;
  } catch (error) {
    console.error('Error generating monthly invoices:', error);
    throw error;
  }
};

// ============ INVOICE PDF & EMAIL ============

/**
 * Fixed Invoice Service - PDF Download Section
 * Replace the download_invoice_pdf function and related code in your invoice_service.ts
 */

// ============ FIXED PDF & EMAIL SECTION ============

/**
 * Download invoice as PDF
 * @param invoice_id - Invoice ID
 */
export const download_invoice_pdf = async (
  invoice_id: number
): Promise<Blob> => {
  try {
    console.log(`Downloading PDF for invoice ${invoice_id}...`);
    
    // Make the request with proper blob handling
    // api_service.get returns response.data, which IS the blob
    const blob = await api_service.get<Blob>(`/billing/invoices/${invoice_id}/pdf/`, {
      responseType: 'blob'
    });
    
    console.log('Received blob:', {
      type: blob?.type,
      size: blob?.size,
      is_blob: blob instanceof Blob
    });
    
    // Check if it's actually a blob
    if (!(blob instanceof Blob)) {
      console.error('Response is not a blob:', blob);
      throw new Error('Response is not a valid Blob');
    }
    
    // Check if blob is empty
    if (blob.size === 0) {
      throw new Error('Received empty PDF file');
    }
    
    // Check if it's actually JSON error disguised as blob
    if (blob.type === 'application/json') {
      const text = await blob.text();
      console.error('Received JSON error as blob:', text);
      
      try {
        const error_data = JSON.parse(text);
        throw new Error(error_data.error || error_data.detail || 'PDF generation failed');
      } catch (parse_error) {
        throw new Error(text || 'PDF generation failed');
      }
    }
    
    // Verify it's a PDF
    if (!blob.type.includes('pdf') && blob.type !== 'application/octet-stream') {
      console.warn('Unexpected blob type:', blob.type);
      // Don't throw error as some servers might not set the correct MIME type
    }
    
    return blob;
  } catch (error: any) {
    console.error('Error downloading invoice PDF:', error);
    
    // Handle blob errors specially
    if (error?.response?.data instanceof Blob) {
      await handleBlobError(error);
    }
    
    // Re-throw with better error message
    if (error?.message) {
      throw error;
    } else {
      throw new Error('Failed to download PDF: ' + (error?.toString() || 'Unknown error'));
    }
  }
};

/**
 * Get invoice PDF URL
 * @param invoice_id - Invoice ID
 */
export const get_pdf_url = (invoice_id: number): string => {
  return `/billing/invoices/${invoice_id}/pdf/`;
};

/**
 * Send invoice via email
 * @param payload - Email payload with recipient and message
 */
export const send_invoice_email = async (
  payload: InvoiceEmailPayload
): Promise<{ message: string; success: boolean }> => {
  try {
    const response = await api_service.post(
      `/billing/invoices/${payload.invoice_id}/send-email/`,
      {
        email_addresses: [payload.recipient_email, ...(payload.cc_emails || [])],
        subject: payload.subject,
        message: payload.message
      }
    );
    return response.results;
  } catch (error) {
    console.error('Error sending invoice email:', error);
    throw error;
  }
};

/**
 * Send invoice via email (legacy method)
 * @param invoice_id - Invoice ID
 * @param email_addresses - Recipient email addresses
 * @param message - Custom message
 */
export const send_email = async (
  invoice_id: number,
  email_addresses?: string[],
  message?: string
): Promise<{ message: string }> => {
  try {
    const response = await api_service.post(`/billing/invoices/${invoice_id}/send-email/`, {
      email_addresses,
      message
    });
    return response.results;
  } catch (error) {
    console.error('Error sending invoice email:', error);
    throw error;
  }
};

/**
 * Bulk send invoices via email
 * @param invoice_ids - Array of invoice IDs
 */
export const bulk_send_emails = async (
  invoice_ids: number[]
): Promise<{ sent: number; failed: number }> => {
  try {
    const response = await api_service.post('/billing/invoices/bulk-send-emails/', {
      invoice_ids
    });
    return response.results;
  } catch (error) {
    console.error('Error bulk sending emails:', error);
    throw error;
  }
};

/**
 * Send payment reminder
 * @param invoice_id - Invoice ID
 */
export const send_reminder = async (
  invoice_id: number
): Promise<{ message: string }> => {
  try {
    const response = await api_service.post(`/billing/invoices/${invoice_id}/send-reminder/`, {});
    return response.results;
  } catch (error) {
    console.error('Error sending reminder:', error);
    throw error;
  }
};

// ============ INVOICE ITEMS ============

/**
 * Add item to invoice
 * @param invoice_id - Invoice ID
 * @param item - Invoice item data
 */
export const add_item = async (
  invoice_id: number,
  item: Omit<InvoiceItem, 'id'>
): Promise<Invoice> => {
  try {
    const response = await api_service.post(`/billing/invoices/${invoice_id}/items/`, item);
    return response.results;
  } catch (error) {
    console.error('Error adding invoice item:', error);
    throw error;
  }
};

/**
 * Update invoice item
 * @param invoice_id - Invoice ID
 * @param item_id - Item ID
 * @param data - Item update data
 */
export const update_item = async (
  invoice_id: number,
  item_id: number,
  data: Partial<InvoiceItem>
): Promise<Invoice> => {
  try {
    const response = await api_service.patch(
      `/billing/invoices/${invoice_id}/items/${item_id}/`,
      data
    );
    return response.results;
  } catch (error) {
    console.error('Error updating invoice item:', error);
    throw error;
  }
};

/**
 * Remove item from invoice
 * @param invoice_id - Invoice ID
 * @param item_id - Item ID
 */
export const remove_item = async (
  invoice_id: number,
  item_id: number
): Promise<Invoice> => {
  try {
    const response = await api_service.delete(
      `/billing/invoices/${invoice_id}/items/${item_id}/`
    );
    return response.results;
  } catch (error) {
    console.error('Error removing invoice item:', error);
    throw error;
  }
};

// ============ INVOICE TEMPLATES ============

/**
 * Fetch invoice template configuration
 */
export const fetch_invoice_template_config = async (): Promise<InvoiceTemplateConfig> => {
  try {
    const response = await api_service.get('/billing/invoices/template_config/');

    console.log("template ", response)
    return response;
  } catch (error) {
    console.error('Error fetching invoice template config:', error);
    throw error;
  }
};

/**
 * Fetch all invoice templates
 */
export const fetch_invoice_templates = async (): Promise<InvoiceTemplate[]> => {
  try {
    const response = await api_service.get('/billing/invoices/templates/');

    return response.results;
  } catch (error) {
    console.error('Error fetching invoice templates:', error);
    throw error;
  }
};

/**
 * Create invoice template
 * @param data - Template data
 */
export const create_invoice_template = async (
  data: Partial<InvoiceTemplate>
): Promise<InvoiceTemplate> => {
  try {
    const response = await api_service.post('/billing/invoices/templates/', data);
    return response.results;
  } catch (error) {
    console.error('Error creating invoice template:', error);
    throw error;
  }
};

/**
 * Update invoice template
 * @param template_id - Template ID
 * @param data - Template update data
 */
export const update_invoice_template = async (
  template_id: number,
  data: Partial<InvoiceTemplate>
): Promise<InvoiceTemplate> => {
  try {
    const response = await api_service.patch(`/billing/invoices/templates/${template_id}/`, data);
    return response.results;
  } catch (error) {
    console.error('Error updating invoice template:', error);
    throw error;
  }
};

// ============ STATISTICS & REPORTS ============

/**
 * Fetch invoice statistics
 * @param date_from - Start date
 * @param date_to - End date
 */
export const get_stats = async (
  date_from?: string,
  date_to?: string
): Promise<InvoiceStatistics> => {
  try {
    const response = await api_service.get('/billing/invoices/stats/', {
      params: { date_from, date_to }
    });
    return response.results;
  } catch (error) {
    console.error('Error fetching invoice stats:', error);
    throw error;
  }
};

/**
 * Fetch overdue invoices
 */
export const get_overdue_invoices = async (): Promise<Invoice[]> => {
  try {
    const response = await api_service.get('/billing/invoices/overdue/');
    return response.results;
  } catch (error) {
    console.error('Error fetching overdue invoices:', error);
    throw error;
  }
};

/**
 * Fetch upcoming invoices (near due date)
 * @param days - Number of days ahead to look
 */
export const get_upcoming_invoices = async (
  days: number = 7
): Promise<Invoice[]> => {
  try {
    const response = await api_service.get('/billing/invoices/upcoming/', {
      params: { days }
    });
    return response.results;
  } catch (error) {
    console.error('Error fetching upcoming invoices:', error);
    throw error;
  }
};

/**
 * Fetch revenue by period
 * @param period - Period type
 * @param date_from - Start date
 * @param date_to - End date
 */
export const get_revenue_by_period = async (
  period: 'day' | 'week' | 'month' | 'year',
  date_from?: string,
  date_to?: string
): Promise<Array<{ period: string; amount: number; count: number }>> => {
  try {
    const response = await api_service.get('/billing/invoices/revenue-by-period/', {
      params: { period, date_from, date_to }
    });
    return response.results;
  } catch (error) {
    console.error('Error fetching revenue by period:', error);
    throw error;
  }
};

/**
 * Generate aging report
 */
export const get_aging_report = async (): Promise<InvoiceAgingReport> => {
  try {
    const response = await api_service.get('/billing/invoices/aging-report/');
    return response.results;
  } catch (error) {
    console.error('Error fetching aging report:', error);
    throw error;
  }
};

/**
 * Generate revenue report
 * @param date_from - Start date
 * @param date_to - End date
 */
export const get_revenue_report = async (
  date_from: string,
  date_to: string
): Promise<RevenueReport> => {
  try {
    const response = await api_service.get('/billing/invoices/revenue-report/', {
      params: { date_from, date_to }
    });
    return response.results;
  } catch (error) {
    console.error('Error fetching revenue report:', error);
    throw error;
  }
};

// ============ VALIDATION & PREVIEW ============

/**
 * Preview invoice before creation
 * @param data - Invoice data
 */
export const preview_invoice = async (
  data: InvoiceCreateData
): Promise<InvoicePreview> => {
  try {
    const response = await api_service.post('/billing/invoices/preview/', data);
    return response.results;
  } catch (error) {
    console.error('Error previewing invoice:', error);
    throw error;
  }
};

/**
 * Validate invoice number
 * @param invoice_number - Invoice number to validate
 * @param exclude_id - Invoice ID to exclude from check
 */
export const validate_invoice_number = async (
  invoice_number: string,
  exclude_id?: number
): Promise<{ is_valid: boolean; message?: string }> => {
  try {
    const response = await api_service.get('/billing/invoices/validate-number/', {
      params: {
        invoice_number,
        exclude: exclude_id
      }
    });
    return response.results;
  } catch (error) {
    console.error('Error validating invoice number:', error);
    throw error;
  }
};

/**
 * Validate invoice data
 * @param data - Invoice data
 */
export const validate_invoice = async (
  data: InvoiceCreateData
): Promise<InvoiceValidation> => {
  try {
    const response = await api_service.post('/billing/invoices/validate/', data);
    return response.results;
  } catch (error) {
    console.error('Error validating invoice:', error);
    throw error;
  }
};

// ============ IMPORT/EXPORT ============

/**
 * Export invoices to CSV/Excel
 * @param format - Export format
 * @param filters - Optional filters
 */
export const export_invoices = async (
  format: 'csv' | 'excel',
  filters?: InvoiceFilters
): Promise<Blob> => {
  try {
    const response = await api_service.get(`/billing/invoices/export/${format}/`, {
      params: filters,
      responseType: 'blob'
    });
    return response.results;
  } catch (error) {
    console.error('Error exporting invoices:', error);
    throw error;
  }
};

// ============ CLASS-BASED SERVICE (backward compatibility) ============

class InvoiceService {
//   private readonly BASE_URL = '/invoices';

  get_invoices = get_invoices;
  get_invoice = get_invoice;
  get_invoice_by_number = get_invoice_by_number;
  create_invoice = create_invoice;
  generate_from_reading = generate_from_reading;
  bulk_generate_invoices = bulk_generate_invoices;
  generate_monthly_invoices = generate_monthly_invoices;
  update_invoice = update_invoice;
  delete_invoice = delete_invoice;
  update_status = update_status;
  mark_as_sent = mark_as_sent;
  mark_as_paid = mark_as_paid;
  cancel_invoice = cancel_invoice;
  download_pdf = download_invoice_pdf;
  get_pdf_url = get_pdf_url;
  send_email = send_email;
  bulk_send_emails = bulk_send_emails;
  send_reminder = send_reminder;
  add_item = add_item;
  update_item = update_item;
  remove_item = remove_item;
  get_stats = get_stats;
  get_overdue_invoices = get_overdue_invoices;
  get_upcoming_invoices = get_upcoming_invoices;
  get_revenue_by_period = get_revenue_by_period;
  export_invoices = export_invoices;
  get_aging_report = get_aging_report;
  preview_invoice = preview_invoice;
  validate_invoice_number = validate_invoice_number;
}

// Export singleton instance
export const invoice_service = new InvoiceService();

// Default export
export default invoice_service;